<template>
  <div class="flex items-start gap-3 w-full">
    <!--
      左侧 label：用 items-start + pt-1.5 让文字垂直中心对齐到 slider track (h-8) 的中心 y=16。
      右侧 Button 高度本身就是 32px（size-icon-sm），自然与 track 同高，无需额外调整。
    -->
    <span
      v-if="label"
      class="shrink-0 pt-1.5 text-s-medium text-text-glass-default whitespace-nowrap"
    >
      {{ label }}
    </span>

    <!-- 中间 slider + 刻度行 -->
    <div class="flex-1 relative min-w-0">
      <Slider
        :model-value="sliderValue"
        :min="min"
        :max="max"
        :step="step"
        :disabled="disabled"
        thumb-alignment="contain"
        range-alignment="thumb-end"
        :thumb-size="THUMB_WIDTH"
        animated
        class="w-full relative"
        track-class="bg-neutral-light-hover h-8"
        range-class="bg-white"
        thumb-class="bg-transparent rounded-none"
        @update:model-value="onDrag"
        @value-commit="onCommit"
      >
        <template #thumb>
          <div class="pointer-events-none relative flex items-center justify-center size-full">
            <div
              class="rounded-10xl flex justify-center items-center"
              :class="isClosed ? 'size-full bg-white' : 'size-4 bg-blue-600'"
            >
              <Icon v-if="isClosed" icon="Status-Wireframe-Close" class="size-4" />
            </div>
          </div>
        </template>
      </Slider>

      <!--
        刻度行：min/max 两端走文字标签（关闭 / 强），中间每个 step 用竖线表示。
        定位基准与 thumb 中心一致：min 位置 = THUMB_HALF，max 位置 = 100% - THUMB_HALF。
      -->
      <div class="relative mt-2 h-4">
        <span
          class="absolute -translate-x-1/2 text-s-medium text-text-glass-default whitespace-nowrap"
          :style="{ left: `calc(${THUMB_HALF}px)` }"
        >
          {{ $t('rt.common.off') }}
        </span>
        <span
          v-for="tick in intermediateTicks"
          :key="tick.value"
          class="absolute -translate-x-1/2 flex items-center justify-center px-1 cursor-pointer"
          :style="{
            left: `calc(${THUMB_HALF}px + ${tick.percent / 100} * (100% - ${THUMB_WIDTH}px))`,
          }"
          @click="jumpToTick(tick.value)"
        >
          <span class="block w-px h-3 bg-text-glass-default" />
        </span>
        <span
          class="absolute -translate-x-1/2 text-s-medium text-text-glass-default whitespace-nowrap"
          :style="{ left: `calc(100% - ${THUMB_HALF}px)` }"
        >
          {{ $t('rt.common.strong') }}
        </span>
      </div>
    </div>

    <slot name="input"> </slot>

    <!-- 右侧 还原按钮：与 TravelCard 的还原按钮样式一致 -->
    <RtRestoreButton
      color="text-b-w"
      :disabled="disabled || value === undefined || value === effectiveDefault"
      @confirm="resetToDefault"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Icon, Slider } from '@rapoo/element'
  import RtRestoreButton from '../RtRestoreButton.vue'

  type Props = {
    /** 行首文案，例如「按下」「释放」 */
    label?: string
    /** 当前值（父组件根据 mode 解析后的单值） */
    value?: number
    /** 还原按钮的目标值，未传时回退到 `min` */
    defaultValue?: number
    min?: number
    max?: number
    step?: number
    disabled?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    min: 0,
    max: 10,
    step: 1,
    defaultValue: undefined,
    disabled: false,
  })

  const emit = defineEmits<{
    /** 拖动中的临时值，仅用于联动预览，不触发设备写入。 */
    preview: [value: number]
    /** 用户完成一次有效变更（拖拽结束 / 点击刻度 / 点击还原）后触发，载荷为新值 */
    commit: [value: number]
  }>()

  /** thumb 尺寸：由 Slider 同时用于视觉尺寸和 range 对齐计算。 */
  const THUMB_WIDTH = 32
  const THUMB_HALF = THUMB_WIDTH / 2

  const sliderValue = ref<number[]>(props.value === undefined ? [] : [props.value])
  const isClosed = computed(() => sliderValue.value[0] === props.min)

  /**
   * 中间刻度位置集合：min/max 两端由文字标签（关闭 / 强）占据，
   * 其余每个 step 在刻度行上以竖线表示。
   */
  const intermediateTicks = computed(() => {
    const result: { value: number; percent: number }[] = []
    const range = props.max - props.min
    if (range <= 0 || props.step <= 0) return result
    // 用浮点累加再按 step 量化，避免 (i * step) 累积误差导致漏掉 max
    const count = Math.round(range / props.step)
    for (let i = 1; i < count; i++) {
      const value = props.min + i * props.step
      result.push({ value, percent: (i / count) * 100 })
    }
    return result
  })

  /** 实际使用的 defaultValue：未提供时回退到 min */
  const effectiveDefault = computed(() => props.defaultValue ?? props.min)

  /** 外部值变化时同步到内部 sliderValue，避免双向不同步 */
  watch(
    () => props.value,
    (v) => {
      sliderValue.value = v === undefined ? [] : [v]
    },
    { immediate: true },
  )

  function snapValue(value: number): number {
    const next = Math.round(value / props.step) * props.step
    return Math.min(props.max, Math.max(props.min, next))
  }

  function onDrag(value?: number[]) {
    if (value?.[0] === undefined) return
    const next = snapValue(value[0])
    sliderValue.value = [next]
    emit('preview', next)
  }

  function onCommit(value: number[]) {
    if (value[0] === undefined) return
    const next = snapValue(value[0])
    sliderValue.value = [next]
    emit('commit', next)
  }

  function jumpToTick(value: number) {
    const snapped = snapValue(value)
    sliderValue.value = [snapped]
    emit('commit', snapped)
  }

  function resetToDefault() {
    const target = snapValue(effectiveDefault.value)
    sliderValue.value = [target]
    emit('commit', target)
  }
</script>
